/**
 * Send DTMF tones.
 * @method sendDTMF
 * @param {Object} DTMFOptions The DTMF options.
 * @param {string} DTMFOptions.tone DTMF tone to send. Valid values are [0,1,2,3,4,5,6,7,8,9,#].
 * @param {number} DTMFOptions.duration=100 The amount of time, in milliseconds, that each DTMF tone should last.
 * @param {number} DTMFOptions.intertoneGap=70 The length of time, in milliseconds, to wait between tones.
 * @param {Object} [sendOptions] The send options.
 * @param {func} [sendOptions.callback] Optional callback for tone event .
 * @param {string} [sendOptions.trackId] The trackId of the sender to use.
 * @return {Boolean} Whether the DTMF tones were inserted
 */
export default function sendDTMF ({ tone, duration = 100, intertoneGap = 70 }, { callback, trackId }) {
  const { proxyPeer, log } = this
  log.info('Sending DTMF tones.', tone)

  if (!proxyPeer.getSenders) {
    log.info('Failed to send tones; getSenders is not supported by this browser.')
    return false
  }
  const senders = proxyPeer.getSenders()
  // Use the trackId if it was provided
  if (trackId) {
    const sender = senders.find(sender => sender.track.id === trackId)
    if (!sender) {
      log.info(`Failed to send tones; could not find track ${trackId}.`)
      return false
    }
    insertDTMF(sender, tone, duration, intertoneGap, callback, log)
    return true
  } else {
    let result
    for (let i = 0; i < senders.length; i++) {
      result = insertDTMF(senders[i], tone, duration, intertoneGap, callback, log)
      if (result) {
        return true
      }
    }

    log.info('Failed to send tones; could not find an appropriate track.')
    return false
  }
}

/**
 * Helper function to sendDTMF tones .
 * @private
 * @method insertDTMF
 * @param {sender} object
 * @param {string} tone
 * @param {number} duration
 * @param {number} intertoneGap
 * @param {Function} callback
 */
function insertDTMF (sender, tone, duration, intertoneGap, callback, log) {
  /**
   * Event handler when tone is played.
   * @private
   * @method handleToneChangeEvent
   * @param  {event} event
   */
  function handleToneChangeEvent (event) {
    if (event.tone !== '') {
      log.debug('Tone played: ' + event.tone)
    } else {
      log.debug('All tones have played.')
    }
  }

  if (sender.dtmf) {
    const dtmfSender = sender.dtmf
    if (callback) {
      dtmfSender.ontonechange = callback
    } else {
      dtmfSender.ontonechange = handleToneChangeEvent
    }
    try {
      dtmfSender.insertDTMF(tone, duration, intertoneGap)
      return true
    } catch (err) {
      log.debug(err.message)
      return false
    }
  } else {
    log.debug('The sender requires DTMF which is not support by this browser.')
    return false
  }
}
